import { Link } from "react-router-dom";
import { Section, Card, CTA } from "@/components/Section";
import { ArrowRight, Mic2, Users, GraduationCap, Building2, Video, Presentation, Compass, Target, ClipboardCheck, Repeat } from "lucide-react";
import EventAnnouncement from "@/components/EventAnnouncement";

const FORMATS = [
  { icon: Mic2, title: "Executive Keynotes" },
  { icon: GraduationCap, title: "Leadership Institutes" },
  { icon: Users, title: "Executive-Team Workshops" },
  { icon: Compass, title: "Facilitated Retreats" },
  { icon: Presentation, title: "Conference Breakouts" },
  { icon: Building2, title: "Custom Organizational Programs" },
  { icon: Video, title: "Virtual Cohorts" },
  { icon: Users, title: "Multi-Session Programs" },
];

const LAB_TIERS = [
  {
    n: "Tier 1",
    t: "Diagnose Before You Design",
    b: "Root-cause analysis of the leadership challenge, before any tool, template, or AI prompt enters the room.",
    accent: "#C6A85A",
    icon: Compass,
  },
  {
    n: "Tier 2",
    t: "Augmented Decision-Making",
    b: "AI is introduced only after the leader diagnoses the issue. Then it widens analysis, sharpens options, and stress-tests thinking, always with the executive at the center.",
    accent: "#E33278",
    icon: Target,
  },
  {
    n: "Tier 3",
    t: "Disciplined Execution",
    b: "Execution readiness and value creation using APPLY → TIMELINE → SUCCESS CHECK → REFINE → EVALUATE. Leaders leave with a working plan, not a theoretical framework.",
    accent: "#0F1D3D",
    icon: ClipboardCheck,
  },
];

const EXECUTION_STEPS = [
  { k: "APPLY", d: "Take the tool built in the Lab into a real leadership context this week." },
  { k: "TIMELINE", d: "Define when and where it will be used, with clear checkpoints." },
  { k: "SUCCESS CHECK", d: "Define what success actually looks like, tied to an outcome, not activity." },
  { k: "REFINE", d: "Adjust the tool based on what real use reveals. This is disciplined iteration." },
  { k: "EVALUATE", d: "Measure success strictly by realization of the intended enterprise outcome." },
];

export default function Training() {
  return (
    <div data-testid="page-training">
      {/* Hero */}
      <Section
        eyebrow="Training & Speaking"
        title="Hands-on experiences that turn leadership authority into practical AI capacity."
        kicker="Every LAYOR training is interactive, anchored to authentic leadership challenges, and produces tools participants build themselves and can begin using immediately. Participants use their preferred AI platform (such as ChatGPT), so the skills stay with them and their team."
      />

      {/* Upcoming complimentary event */}
      <EventAnnouncement testid="training-event-announcement" size="hero" />

      {/* Flagship: Executive Strategy Lab */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#0A152C" }}>
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 82% 20%, rgba(198,168,90,0.14), transparent 55%), radial-gradient(circle at 12% 82%, rgba(227,50,120,0.10), transparent 55%)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-24">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <div
                className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[9px] uppercase tracking-[0.35em] font-semibold"
                style={{ backgroundColor: "#E6C871", color: "#0A152C" }}
                data-testid="flagship-strategy-lab-badge"
              >
                Flagship Program
              </div>
              <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.05] text-white">
                Executive Strategy Lab™: Leadership Velocity
              </h2>
              <p className="mt-3 text-[var(--layor-gold)] text-sm md:text-base font-semibold">
                Driving Enterprise Outcomes with Practical AI™.
              </p>
              <p className="mt-5 text-white/80 leading-relaxed max-w-2xl">
                A structured, hands-on 90-minute virtual experience in which executive leaders move through three tiers designed to translate leadership authority into decisive, AI-enabled action they can carry into the very next week. Leadership first, practical AI as a way to extend that leadership further.
              </p>
              <p className="mt-4 text-[var(--layor-gold)] text-sm leading-relaxed">
                Pricing is customized based on the scope and needs of each engagement. Please complete the Quote Request form, and a member of the LAYOR Digital Group team will contact you to discuss your needs and the appropriate options.
              </p>
              <p className="mt-4 text-white/85 italic text-sm leading-relaxed max-w-2xl">
                The LAYOR takeaway: leaders leave with strategic clarity, a shared language for practical AI, and a decision they can act on within the very next week.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/quote?service=Executive%20Strategy%20Lab%20Leadership%20Velocity"
                  className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                  data-testid="cta-strategy-lab-flagship"
                >
                  Host a Strategy Lab Session <ArrowRight size={14} />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold border border-white/25 text-white hover:bg-white hover:text-[var(--layor-navy)]"
                >
                  Book a Consultation
                </Link>
              </div>
            </div>
            <div className="md:col-span-5">
              <div
                className="rounded-2xl border p-6"
                style={{
                  borderColor: "rgba(198,168,90,0.35)",
                  backgroundColor: "rgba(255,255,255,0.04)",
                  backdropFilter: "blur(6px)",
                }}
              >
                <div
                  className="text-[10px] uppercase tracking-[0.35em] font-semibold"
                  style={{ color: "#E6C871" }}
                >
                  What Strategy Labs Include
                </div>
                <ul className="mt-4 space-y-3 text-white/90 text-sm leading-snug">
                  {[
                    "90-minute structured virtual session",
                    "Tier 1: Diagnose Before You Design",
                    "Tier 2: Augmented Decision-Making",
                    "Tier 3: Disciplined Execution framework",
                    "Participants use their preferred AI platform",
                    "A decision leaders can act on within the next week",
                  ].map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <span
                        className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: "#E33278" }}
                      />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[3px] flex">
          <div className="flex-[4]" style={{ backgroundColor: "#C6A85A" }} />
          <div className="flex-1" style={{ backgroundColor: "#E33278" }} />
        </div>
      </section>

      {/* Executive Strategy Lab tiers + framework */}
      <Section
        eyebrow="Signature 90-Minute Experience"
        title="The three tiers of the Executive Strategy Lab™."
        kicker="A structured, hands-on 90-minute virtual session in which executive leaders move through three tiers designed to translate leadership authority into decisive, AI-enabled action they can carry into the very next week."
      >
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {LAB_TIERS.map(({ n, t, b, accent, icon: Icon }) => (
            <div
              key={n}
              className="rounded-2xl border border-[var(--layor-border)] bg-white p-7"
              data-testid={`lab-tier-${n.toLowerCase().replace(" ", "-")}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl"
                  style={{ backgroundColor: accent, color: "#fff" }}
                >
                  <Icon size={18} />
                </div>
                <div
                  className="text-[10px] uppercase tracking-[0.35em] font-semibold"
                  style={{ color: accent }}
                >
                  {n}
                </div>
              </div>
              <h3 className="mt-5 font-display text-lg text-[var(--layor-navy)] leading-snug">
                {t}
              </h3>
              <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed">{b}</p>
            </div>
          ))}
        </div>

        {/* Execution framework */}
        <div className="mt-10 rounded-3xl border border-[var(--layor-border)] bg-[var(--layor-ivory)] p-6 md:p-8">
          <div className="flex items-center gap-3">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--layor-navy)] text-[var(--layor-gold)]">
              <Repeat size={18} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.35em] font-semibold text-[var(--layor-navy)]">
              The Execution Framework
            </div>
          </div>
          <h3 className="mt-4 font-display text-xl md:text-2xl text-[var(--layor-navy)] leading-snug">
            APPLY → TIMELINE → SUCCESS CHECK → REFINE → EVALUATE
          </h3>
          <div className="mt-6 grid md:grid-cols-5 gap-3">
            {EXECUTION_STEPS.map((s, i) => (
              <div key={s.k} className="rounded-xl border border-[var(--layor-border)] bg-white p-4">
                <div
                  className="text-[10px] uppercase tracking-[0.28em] font-semibold"
                  style={{ color: i === 4 ? "#E33278" : "#C6A85A" }}
                >
                  {s.k}
                </div>
                <p className="mt-2 text-xs text-[var(--layor-navy)]/80 leading-snug">{s.d}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-8 max-w-3xl text-[15px] italic text-[var(--layor-pink)] leading-relaxed">
          The LAYOR takeaway: leaders leave with a decision they can act on, a plan they can defend, and the confidence to move forward, with practical AI extending their leadership expertise, not competing with it.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/quote?service=Executive%20Strategy%20Lab%20Leadership%20Velocity"
            className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
            data-testid="cta-strategy-lab"
          >
            Host a Strategy Lab Session <ArrowRight size={14} />
          </Link>
          <Link
            to="/contact"
            className="btn-ghost inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
          >
            Book a Consultation
          </Link>
        </div>
      </Section>

      {/* Executive AI Leadership Institute (customized multi-session, not flagship) */}
      <Section
        eyebrow="Customized Multi-Session Engagement"
        title="LAYOR Executive AI Leadership Institute™"
        kicker="A premium, customized, multi-session institutional offering for executive teams, associations, and organizations building durable AI leadership capacity. Every Institute is diagnosed, designed, and delivered around the specific enterprise outcome the organization is pursuing."
        className="bg-white border-y border-[var(--layor-border)]"
      >
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <div className="eyebrow" style={{ color: "#C6A85A" }}>What Institutes Include</div>
            <ul className="mt-4 space-y-3 text-sm text-[var(--layor-navy)]/85 leading-snug">
              {[
                "Executive-team strategic discovery & diagnosis",
                "Customized AI leadership curriculum",
                "Facilitated multi-session progression",
                "Live builds of practical leadership tools",
                "Implementation coaching between sessions",
                "Outcome measurement tied to enterprise goals",
              ].map((it) => (
                <li key={it} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: "#E33278" }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card>
            <div className="eyebrow" style={{ color: "#E33278" }}>How It Works</div>
            <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed">
              Pricing is customized based on the scope and needs of each engagement. Please complete the Quote Request form, and a member of the LAYOR Digital Group team will contact you to discuss your priorities and the appropriate options.
            </p>
            <p className="mt-4 text-[13px] italic text-[var(--layor-pink)] leading-relaxed">
              The LAYOR takeaway: your leadership team leaves with strategic clarity, a shared language for practical AI, and the tools to keep moving forward long after the Institute ends.
            </p>
            <Link
              to="/quote?service=LAYOR%20Executive%20AI%20Leadership%20Institute"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--layor-navy)] hover:text-[var(--layor-pink)]"
              data-testid="cta-institute"
            >
              Inquire about the Institute <ArrowRight size={14} />
            </Link>
          </Card>
        </div>
      </Section>

      {/* Leadership Lab + Formats */}
      <Section
        eyebrow="Additional Ways to Engage"
        title="From a 120-minute team experience to a multi-month engagement."
        className="bg-white border-y border-[var(--layor-border)]"
      >
        <div className="grid md:grid-cols-2 gap-5 mb-10">
          <Card>
            <div className="eyebrow" style={{ color: "#C6A85A" }}>Team Experience</div>
            <h3 className="mt-3 font-display text-xl text-[var(--layor-navy)] leading-snug">
              LAYOR Leadership Lab™
            </h3>
            <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed">
              A 120-minute hands-on experience for a leadership team. Leaders build a Leadership
              Communication Builder, an AI Leadership Thought Partner, a Personal AI Assistant
              Blueprint, and the LAYOR Prompt Blueprint around real responsibilities they already
              carry, and leave with a 24-hour implementation commitment before adjournment.
            </p>
            <p className="mt-3 text-[13px] italic text-[var(--layor-pink)] leading-relaxed">
              The LAYOR takeaway: your team leaves with practical tools they built themselves,
              ready to lead forward tomorrow with clarity and confidence.
            </p>
            <Link
              to="/quote?service=LAYOR%20Leadership%20Lab"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--layor-navy)] hover:text-[var(--layor-pink)]"
            >
              Bring the Leadership Lab to your team <ArrowRight size={14} />
            </Link>
          </Card>
          <Card>
            <div className="eyebrow" style={{ color: "#E33278" }}>Enterprise Engagement</div>
            <h3 className="mt-3 font-display text-xl text-[var(--layor-navy)] leading-snug">
              Multi-Session Executive Cohorts &amp; Retreats
            </h3>
            <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed">
              Facilitated retreats and sequenced cohort programs for cabinets, boards, and
              executive teams. Progressive skill-building, cohort accountability, and a growing
              library of leadership-ready AI resources, always customized to the organization.
            </p>
            <p className="mt-3 text-[13px] italic text-[var(--layor-pink)] leading-relaxed">
              The LAYOR takeaway: an executive team that thinks, decides, and executes together,
              with practical AI extending, not replacing, their leadership judgment.
            </p>
            <Link
              to="/quote?service=Multi-Session%20Executive%20Cohort"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--layor-navy)] hover:text-[var(--layor-pink)]"
            >
              Design an executive engagement <ArrowRight size={14} />
            </Link>
          </Card>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FORMATS.map(({ icon: Icon, title }, i) => (
            <div
              key={title}
              className="rounded-xl border border-[var(--layor-border)] bg-white p-5"
              data-testid={`format-${i}`}
            >
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--layor-navy)] text-[var(--layor-gold)]">
                <Icon size={16} />
              </div>
              <h4 className="mt-4 font-display text-[15px] text-[var(--layor-navy)] leading-snug">
                {title}
              </h4>
            </div>
          ))}
        </div>
      </Section>

      <CTA
        dark
        headline="Invite Patricia to lead your next executive session."
        sub="Share your audience, outcome, and timeline. We will diagnose the right engagement (Institute, Strategy Lab, Leadership Lab, keynote, retreat, or cohort) and propose a tailored path. Pricing is customized based on the scope and needs of each engagement, and our team will follow up to discuss your priorities and the appropriate options."
        actions={
          <>
            <Link
              to="/quote?service=Keynote%20or%20Conference%20Presentation"
              className="btn-gold rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center gap-2"
              data-testid="training-invite-speaker"
            >
              Invite Patricia to Speak <ArrowRight size={14} />
            </Link>
            <Link
              to="/quote?service=Training%20or%20Workshop"
              className="rounded-full px-5 py-3 text-sm font-semibold border border-white/30 text-white hover:bg-white hover:text-[var(--layor-navy)]"
            >
              Request Training
            </Link>
          </>
        }
      />
    </div>
  );
}
